import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { LeadId, Task, TaskStatus } from '@keyvue/one-api-client';
import useClient, { type ReactiveCollection } from '@composables/useClient';

/** The tasks of one lead, or of all leads of one client. */
export type TaskScope = { lead: LeadId } | { 'lead.primaryContact': string };

/** At most this many completed tasks are loaded, the most recent first. */
export const COMPLETED_PAGE_SIZE = 100;

export interface TaskOverview {
    /** Open and canceled tasks: what the task lists show unless "Voltooid" is picked. */
    active: ReactiveCollection<Task>;
    /** The completed tasks, newest first, at most COMPLETED_PAGE_SIZE. Empty until loadCompleted(). */
    completed: ReactiveCollection<Task>;
    /** How many completed tasks there are in all. */
    completedCount: Ref<number>;
    /** The completed task with the latest due date, or null. */
    latestCompleted: ComputedRef<Task | null>;
    /** Whether loadCompleted() has run, so `completed` holds the list. */
    completedLoaded: Ref<boolean>;
    /** Loads the active tasks and the completed summary again, and the completed list once it was loaded. */
    load: () => Promise<void>;
    /** Loads the completed list. */
    loadCompleted: () => Promise<void>;
}

/**
 * A lead's or client's tasks without loading every completed one.
 *
 * Some Realworks clients have thousands of completed tasks; loading them all for the lead page ran past
 * the API's time limit (one client with 1,983 completed tasks, 3 Nov 2026). So a task overview loads the
 * active tasks, plus one small request that gives the number of completed tasks and the most recent one
 * (LeadProgressCard shows it when there is nothing coming up). The completed list itself is loaded when
 * someone asks for it.
 */
export default function useTaskOverview() {
    const { getLazyReactiveCollection } = useClient();

    const getTaskOverview = async (scope: TaskScope): Promise<TaskOverview> => {
        const completedNewestFirst = { ...scope, status: TaskStatus.COMPLETED.value, 'order[dueDateTime]': 'desc', pagination: true };
        const active = getLazyReactiveCollection(Task, { ...scope, status: [TaskStatus.OPEN.value, TaskStatus.CANCELED.value] });
        const summary = getLazyReactiveCollection(Task, { ...completedNewestFirst, itemsPerPage: 1 });
        const completed = getLazyReactiveCollection(Task, { ...completedNewestFirst, itemsPerPage: COMPLETED_PAGE_SIZE });
        const completedLoaded = ref(false);
        // Set before the first completed request, so a reload during it includes the completed list.
        let completedWanted = false;
        // One completed-list request at a time, so an older response can never overwrite a newer one.
        let completedRequests: Promise<void> = Promise.resolve();
        const loadCompletedList = () => {
            const run = completedRequests.then(() => completed.load());
            completedRequests = run.catch(() => undefined);

            return run;
        };

        const loadCompleted = async () => {
            completedWanted = true;
            try {
                await loadCompletedList();
                completedLoaded.value = true;
            } catch (error) {
                completedWanted = completedLoaded.value;
                throw error;
            }
        };

        const load = async () => {
            await Promise.all([active.load(), summary.load(), ...(completedWanted ? [loadCompletedList()] : [])]);
        };

        await load();

        return {
            active,
            completed,
            completedCount: summary.total,
            latestCompleted: computed(() => summary.data.value[0] ?? null),
            completedLoaded,
            load,
            loadCompleted,
        };
    };

    return { getTaskOverview };
}
